//new
import React, { useState, useEffect, useRef, useCallback, useMemo, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import ClassSectionFilter from './parts/ClassSectionFilter';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';
import ViewStudentModal from './parts/ViewStudentModal';
import RegisterStudentModal from './parts/RegisterStudentModal';
import BulkEditStudentsModal from './parts/BulkEditStudentsModal';
import BulkEditPreModal from './parts/BulkEditPreModal';
import PasswordResetModal from './parts/PasswordResetModal';
import ProfilePictureHover from '../../components/ProfilePictureHover';
import SwapProfilePictureModal from './parts/SwapProfilePictureModal';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import BulkRegisterStudentsCSV, { BulkRegisterStudentsCSVHandle } from './parts/BulkRegisterStudentsCSV';
import { showDevTools } from '../../config/env';
import { useExportCartStore, toExportCartStudent } from './parts/useExportCartStore';
import ExportCartPanel from './parts/ExportCartPanel';
import ExportStudentsZip, { StudentExportData, ExportStudentsZipHandle } from './ExportStudentsZip';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface StudentData {
  // `id` is the student's own identity id (Student._id) — this is what
  // View/Edit/Delete/Password-reset all key off, same as before.
  id: string;
  // `sp_student_id` is the public-facing Skoolpillar student id — printed on
  // ID cards and what barcodes/QRs get generated from. Distinct from `id`.
  sp_student_id: string;
  // `student_session_id` is this enrollment record's id (StudentSession._id)
  // — separate from `id` on purpose, since one student has one row per
  // session. Not used by anything yet, but kept for when per-session
  // actions (promotion, roll-number edits, etc.) get built.
  student_session_id: string;
  user_id: string;
  class: string; // legacy alias, equals class_id
  class_id: string;
  class_name: string; // resolved client-side from the classes list, not sent by the API
  section: string | null;
  name: string;
  gender: string;
  email_id: string;
  admission_no: string;
  admission_type: string | null;
  roll_no: number;
  dob: string;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
  created_at: string;
  updated_at: string;
  user_email: string;
  profile_picture_url: string;
}

interface PaginationData {
  current_page: number;
  total_pages: number;
  total_items: number;
  per_page: number;
}

// Backend clamps `limit` to a max per request, so fetching "all" students
// for export has to page through results and concatenate them client-side.
// Adjust if your new backend's clamp differs.
const EXPORT_FETCH_PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// Custom checkbox — replaces the native <input type="checkbox">.
//
// FIX (selection UI-sync bug): the native checkbox's visual state was
// getting silently dropped for the *row* checkboxes when "select all" was
// used, even though `selectedIds` state was correct (row highlight + bulk
// count were always right). Root cause: PrimeReact's DataTable does its own
// per-row diffing on the `body` template output, and toggling `selectedIds`
// alone doesn't change anything PrimeReact treats as row-identity-relevant,
// so re-renders of individual row cells could be skipped/stale — the header
// checkbox re-rendered fine (it's not inside the row-diffed body), but row
// checkboxes could lag behind.
//
// Fix approach: render a fully custom (non-native) checkbox whose checked
// visual is driven purely by props, AND key the wrapping element on the
// checked value itself (see checkboxTemplate below). Keying on `isChecked`
// forces React to treat it as a new element whenever the checked state
// actually changes, guaranteeing the visual updates — without remounting
// the whole DataTable (which caused flicker/re-render of the full list).
// ---------------------------------------------------------------------------
const CheckBox: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  onClick?: (e: React.MouseEvent) => void;
}> = ({ checked, onChange, onClick }) => (
  <div
    role="checkbox"
    aria-checked={checked}
    onClick={(e) => {
      onClick?.(e);
      onChange(!checked);
    }}
    style={{
      width: 17,
      height: 17,
      borderRadius: 4,
      border: `1.5px solid ${checked ? '#3b82f6' : '#d1d5db'}`,
      background: checked ? '#3b82f6' : '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background .12s, border-color .12s',
    }}
  >
    {checked && (
      <svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </div>
);

const StudentsListDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bulkRegisterRef = useRef<BulkRegisterStudentsCSVHandle>(null);
  const exportZipRef = useRef<ExportStudentsZipHandle>(null);

  // Current session comes from the school details store — this dashboard
  // doesn't own that fetch, it just reads whatever session is current.
  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;
  const currentSessionLabel = schoolDetails?.current_session?.session || null;

  // Export cart — persists marked students across class/section navigation.
  const cartCount = useExportCartStore((s) => s.count);
  const addManyToCart = useExportCartStore((s) => s.addMany);

  const [classes, setClasses] = useState<ClassData[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [showAllSections, setShowAllSections] = useState<boolean>(false);
  const [studentsPage, setStudentsPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    per_page: 10,
  });

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentData | null>(null);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordResetTarget, setPasswordResetTarget] = useState<StudentData | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const selectedIdsRef = useRef<Set<string>>(new Set());
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isPreModalOpen, setIsPreModalOpen] = useState(false);
  const [bulkVisibleFields, setBulkVisibleFields] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);

  const [isCartPanelOpen, setIsCartPanelOpen] = useState(false);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const allOnPageChecked = students.length > 0 && students.every((s) => selectedIds.has(s.id));

  // FIX (real root cause): PrimeReact's DataTable memoizes each row/body-cell
  // based on the identity of the row's *data object* — it does NOT know to
  // re-render a row just because a `body` template's closure (checkboxTemplate)
  // changed. Since `students` objects themselves never changed when toggling
  // selection, PrimeReact treated affected rows as "unchanged" and skipped
  // re-rendering their cells — even though our checkboxTemplate closure had
  // the right `selectedIds.has(...)` value. The header checkbox isn't part of
  // this row-memoization path, which is why it always looked correct.
  //
  // Fix: bake the selection flag directly into each row's data object so the
  // object reference changes whenever selection changes — this is exactly
  // the kind of change PrimeReact's row memoization does detect.
  const displayRows = useMemo(
    () => students.map((s) => ({ ...s, __selected: selectedIds.has(s.id) })),
    [students, selectedIds]
  );

  const selectedStudents = students
    .filter((s) => selectedIds.has(s.id))
    .map((s) => ({
      ...s,
      section: s.section ?? '',
      class_name: s.class_name || String(s.class) || '',
      class_id: s.class_id || String(s.class) || '',
    }));

  const swapStudentA = selectedIds.size === 2 ? students.find((s) => s.id === Array.from(selectedIds)[0]) ?? null : null;
  const swapStudentB = selectedIds.size === 2 ? students.find((s) => s.id === Array.from(selectedIds)[1]) ?? null : null;

  const clearSelection = useCallback(() => {
    const empty = new Set<string>();
    selectedIdsRef.current = empty;
    setSelectedIds(empty);
  }, []);

  useEffect(() => {
    clearSelection();
  }, [studentsPage, selectedClass, selectedSection, showAllSections, debouncedSearch, pageSize, currentSessionId]);

  useEffect(() => {
    fetchStudents();
  }, [studentsPage, selectedClass, selectedSection, showAllSections, debouncedSearch, pageSize, currentSessionId]);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => setClasses(loadedClasses);

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const cls = classes.find((c) => c.id === selectedClass);
    return cls ? Boolean(cls.sections?.length) : false;
  };

  // A search query bypasses the class/section gating entirely — searching
  // is meant to work school-wide regardless of what filter is selected,
  // same as the empty-state copy below already implies.
  const shouldFetchStudents = (): boolean => {
    if (debouncedSearch.trim().length > 0) return true;
    if (!selectedClass) return false;
    if (selectedClassHasSections()) return selectedSection !== null || showAllSections;
    return true;
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setStudentsPage(1);
    }, 400);
  };

  const handleShowAllSections = () => {
    setShowAllSections(true);
    setStudentsPage(1);
  };

  // Shared row-mapping — identical response shape from both /students and
  // /students/search (search reuses the exact same aggregation on the
  // backend), so one mapper covers both call sites.
  const mapStudentRow = useCallback((doc: any): StudentData => {
    const matchedClass = classes.find((c) => c.id === doc.class_id);
    return {
      id: doc.student_id ?? doc.id,
      sp_student_id: doc.sp_student_id,
      student_session_id: doc._id,
      user_id: doc.user_id,
      class: doc.class_id,
      class_id: doc.class_id,
      class_name: matchedClass?.class_name || doc.class_name || '',
      section: doc.section || null,
      name: doc.name,
      gender: doc.gender,
      email_id: doc.email_id,
      admission_no: doc.admission_no,
      admission_type: doc.admission_type || null,
      roll_no: doc.roll_no,
      dob: doc.dob,
      blood_group: doc.blood_group,
      father_name: doc.father_name,
      mother_name: doc.mother_name,
      father_no: doc.father_no,
      mother_no: doc.mother_no,
      address: doc.address,
      created_at: doc.created_at,
      updated_at: doc.updated_at,
      user_email: doc.email_id,
      profile_picture_url: doc.profile_picture?.url || '',
    };
  }, [classes]);

  // Shared filter params (class/section) used both by the page fetch and
  // the "export all" fetch, so the two stay in sync. Search uses its own
  // endpoint/param, added separately at each call site.
  const buildFilterParams = useCallback((): Record<string, any> => {
    const params: Record<string, any> = {};
    if (selectedClass) params.class_id = selectedClass;
    if (selectedSection && !showAllSections) params.section = selectedSection;
    return params;
  }, [selectedClass, selectedSection, showAllSections]);

  const fetchStudents = async (): Promise<void> => {
    if (!currentSessionId) {
      // School details (and its current_session) haven't loaded yet —
      // nothing to fetch until that's available.
      setStudents([]);
      return;
    }
    if (!shouldFetchStudents()) {
      setStudents([]);
      return;
    }
    setLoading(true);
    try {
      const trimmedSearch = debouncedSearch.trim();
      const isSearching = trimmedSearch.length > 0;

      const params: any = {
        ...buildFilterParams(),
        page: studentsPage,
        limit: pageSize,
        session_id: currentSessionId,
      };

      // Search hits its own endpoint (/students/search, query param `q`) —
      // separate from the regular listing endpoint (/students), which
      // doesn't do free-text matching. Both return the same response shape
      // (data/pagination/section_counts), so everything below this branch
      // — mapping, pagination, error handling — is shared.
      const endpoint = isSearching ? '/students/search' : '/students';
      if (isSearching) params.q = trimmedSearch;

      const response = await apiClient.get(endpoint, { params });
      const data = response.data;

      if (data.status === 'success') {
        const rawList: any[] = data.data || [];
        const mapped: StudentData[] = rawList.map(mapStudentRow);

        setStudents(mapped);
        setPaginationData({
          current_page: data.pagination.current_page,
          total_pages: data.pagination.total_pages,
          total_items: data.pagination.total_items,
          per_page: data.pagination.per_page,
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: isSearching ? 'Failed to search students' : 'Failed to fetch students',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching students',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetches every student matching the current filters, across ALL pages —
  // used by the "All Students" export scope. Pages through results with the
  // same endpoint choice (search vs listing) as the on-screen fetch.
  const fetchAllStudentsForExport = useCallback(async (): Promise<StudentExportData[]> => {
    if (!currentSessionId) throw new Error('No active session.');

    const trimmedSearch = debouncedSearch.trim();
    const isSearching = trimmedSearch.length > 0;
    const endpoint = isSearching ? '/students/search' : '/students';
    const baseParams: Record<string, any> = {
      ...buildFilterParams(),
      session_id: currentSessionId,
    };
    if (isSearching) baseParams.q = trimmedSearch;

    const firstResponse = await apiClient.get(endpoint, {
      params: { ...baseParams, page: 1, limit: EXPORT_FETCH_PAGE_SIZE },
    });
    const firstData = firstResponse.data;
    if (firstData.status !== 'success') {
      throw new Error(firstData.message || 'Failed to fetch all students');
    }

    let allRaw: any[] = firstData.data ?? [];
    const totalPages: number = firstData.pagination?.total_pages ?? 1;

    if (totalPages > 1) {
      const remainingPageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const remainingResponses = await Promise.all(
        remainingPageNumbers.map((page) =>
          apiClient.get(endpoint, { params: { ...baseParams, page, limit: EXPORT_FETCH_PAGE_SIZE } })
        )
      );
      for (const res of remainingResponses) {
        if (res.data.status === 'success') {
          allRaw = allRaw.concat(res.data.data ?? []);
        } else {
          throw new Error(res.data.message || 'Failed to fetch all students');
        }
      }
    }

    return allRaw.map(mapStudentRow).map(toExportRow);
  }, [currentSessionId, debouncedSearch, buildFilterParams, mapStudentRow]);

  const handleReorderRollNumbers = async () => {
    // Not yet migrated to the new session-scoped backend — left as-is
    // per "deal with PUT/other endpoints later."
    if (!selectedClass) return;
    const classData = classes.find((c) => c.id === selectedClass);
    if (!classData) return;
    try {
      setLoading(true);
      const payload: any = { class_name: classData.class_name };
      if (selectedSection) payload.section = selectedSection;
      const response = await apiClient.post('/reorder-roll-numbers-by-name', payload);
      const data = response.data;
      if (data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Roll numbers reordered!',
          life: 4000,
        });
        fetchStudents();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: 'Failed to reorder roll numbers!',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) return;
    try {
      const response = await apiClient.post('/delete-student-by-user-id', { user_id: userId });
      const data = response.data;
      if (data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Student deleted successfully!',
          life: 4000,
        });
        fetchStudents();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: 'Failed to delete student!',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred',
        life: 5000,
      });
    }
  };

  const handlePasswordReset = async (newPassword: string) => {
    await apiClient.post('/admin/reset-password', {
      user_id: passwordResetTarget?.user_id,
      new_password: newPassword,
    });
  };

  const handleClassChange = (classId: string | null) => {
    setSelectedClass(classId);
    setSelectedSection(null);
    setShowAllSections(false);
    setStudentsPage(1);
    clearSelection();
  };

  const handleSectionChange = (section: string | null) => {
    setSelectedSection(section);
    setShowAllSections(false);
    setStudentsPage(1);
    clearSelection();
  };

  const toggleSelectionMode = () => {
    const next = !selectionMode;
    setSelectionMode(next);
    clearSelection();
    setTableKey((k) => k + 1);
  };

  // FIX: toggleOne/toggleAll no longer force a full DataTable remount
  // (that stopped the "checkbox doesn't visually update" bug but caused
  // the whole list to flicker/re-render on every click). Instead, the
  // checkbox visual correctness now comes from checkboxTemplate keying its
  // wrapper element on the checked value itself (see below) — React then
  // guarantees that specific cell re-renders with the right visual the
  // moment `selectedIds` changes, without touching the rest of the table.
  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      selectedIdsRef.current = next;
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        students.forEach((s) => (checked ? next.add(s.id) : next.delete(s.id)));
        selectedIdsRef.current = next;
        return next;
      });
    },
    [students]
  );

  const handlePreModalConfirm = (fields: string[]) => {
    setBulkVisibleFields(fields);
    setIsBulkEditOpen(true);
  };

  const handleBulkEditSuccess = () => {
    setIsBulkEditOpen(false);
    setBulkVisibleFields([]);
    clearSelection();
    setSelectionMode(false);
    setTableKey((k) => k + 1);
    fetchStudents();
  };

  const handleSwapSuccess = () => {
    clearSelection();
    setSelectionMode(false);
    setTableKey((k) => k + 1);
    fetchStudents();
  };

  // Maps the currently selected rows into the export cart, so you can flip
  // to a different class/section, select more students, and mark those too
  // — the marked set accumulates until you clear it or export it.
  const handleAddSelectedToCart = () => {
    if (selectedIds.size === 0) return;
    const rows = students.filter((s) => selectedIds.has(s.id)).map(toExportRow).map(toExportCartStudent);
    addManyToCart(rows);
    toast.current?.show({
      severity: 'success',
      summary: 'Added to Export Cart',
      detail: `${rows.length} student${rows.length !== 1 ? 's' : ''} marked for export.`,
      life: 3000,
    });
    clearSelection();
  };

  const calculateAge = (dob: string): string => {
    const b = new Date(dob),
      t = new Date();
    let age = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
    return `${age} yr`;
  };

  const getEmptyStateContent = () => {
    if (!currentSessionId)
      return {
        icon: 'pi pi-exclamation-circle',
        title: 'No Active Session',
        message: 'Could not determine the current session for this school. Please refresh and try again.',
      };
    if (debouncedSearch.trim())
      return {
        icon: 'pi pi-search',
        title: 'No Results Found',
        message: `No students matched "${debouncedSearch}".`,
      };
    if (!selectedClass)
      return {
        icon: 'pi pi-filter',
        title: 'No Class Selected',
        message: 'Please select a class from the filter above to view students',
      };
    if (selectedClassHasSections() && !selectedSection && !showAllSections)
      return {
        icon: 'pi pi-list',
        title: 'No Section Selected',
        message: 'Please select a section, or tap "All Sections" to view all students in this class',
      };
    return {
      icon: 'pi pi-inbox',
      title: 'No Students Found',
      message: 'There are no students enrolled in this class/section yet',
    };
  };

  const checkboxHeader = useCallback(
    (): JSX.Element => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CheckBox checked={allOnPageChecked} onChange={(checked) => toggleAll(checked)} />
      </div>
    ),
    [allOnPageChecked, toggleAll]
  );

  // FIX: the wrapper div is keyed on `cb-${rowData.id}-${isChecked}`. This
  // is the actual fix for the reported bug — when `isChecked` flips, the
  // key changes, so React treats it as a brand-new element and mounts it
  // fresh with the correct visual, instead of possibly reusing/skipping a
  // stale cell. No native <input>, no DataTable remount, no full-list
  // flicker — only the affected checkbox cell updates.
  const checkboxTemplate = useCallback(
    (rowData: StudentData & { __selected?: boolean }): JSX.Element => {
      // Read selection off the row object itself (see displayRows above),
      // not off `selectedIds` directly — this is what makes PrimeReact's
      // row memoization actually pick up the change.
      const isChecked = Boolean(rowData.__selected);
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CheckBox
            checked={isChecked}
            onChange={() => toggleOne(rowData.id)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      );
    },
    [toggleOne]
  );

  const serialNumberTemplate = (_: StudentData, options: ColumnBodyOptions): JSX.Element => {
    const index = options.rowIndex + (studentsPage - 1) * pageSize;
    return (
      <div
        style={{
          width: 28,
          height: 28,
          background: '#f9fafb',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 12,
          color: '#6b7280',
          margin: '0 auto',
        }}
      >
        {index + 1}
      </div>
    );
  };

  const studentNameTemplate = (rowData: StudentData): JSX.Element => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ProfilePictureHover
          src={rowData.profile_picture_url}
          name={rowData.name}
          cacheBust={new Date(rowData.updated_at).getTime().toString()}
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: '#1f2937',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {rowData.name}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
            {calculateAge(rowData.dob)}
          </div>
        </div>
      </div>
    );
  };

  const genderTemplate = (rowData: StudentData): JSX.Element => (
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: rowData.gender?.toLowerCase() === 'male' ? '#3b82f6' : '#ec4899',
        textTransform: 'capitalize',
      }}
    >
      {rowData.gender || '—'}
    </div>
  );

  const fatherNameTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>{r.father_name}</div>
  );

  const contactTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, fontFamily: 'monospace' }}>{r.father_no}</div>
  );

  const rollNoTemplate = (r: StudentData) => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        padding: '4px 8px',
        borderRadius: 6,
        background: '#f9fafb',
        fontSize: 12,
        fontWeight: 700,
        color: '#1f2937',
        border: '1px solid #e5e7eb',
      }}
    >
      {r.roll_no}
    </div>
  );

  const admissionNoTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, fontFamily: 'monospace' }}>{r.admission_no}</div>
  );

  const admissionTypeTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, fontFamily: 'monospace' }}>
      {r.admission_type?.trim() || 'N/A'}
    </div>
  );

  const classTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>{r.class_name}</div>
  );

  const sectionTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: r.section ? '#1f2937' : '#9ca3af', fontWeight: r.section ? 600 : 400 }}>
      {r.section && r.section.trim() ? r.section : '—'}
    </div>
  );

  const dobTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{new Date(r.dob).toLocaleDateString()}</div>
  );

  const addressTemplate = (r: StudentData) => (
    <div
      style={{
        fontSize: 13,
        color: r.address?.trim() ? '#1f2937' : '#9ca3af',
        fontWeight: 400,
        maxWidth: 180,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={r.address?.trim() || '—'}
    >
      {r.address?.trim() || '—'}
    </div>
  );

  const actionTemplate = (rowData: StudentData) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-rounded"
        tooltip="View"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#60a5fa', width: 32, height: 32 }}
        onClick={() => {
          setSelectedStudentId(rowData.id);
          setIsViewModalOpen(true);
        }}
      />
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: 32, height: 32 }}
        onClick={() => {
          setEditingStudent(rowData);
          setIsRegisterModalOpen(true);
        }}
      />
      <Button
        icon="pi pi-key"
        className="p-button-text p-button-rounded"
        tooltip="Reset Password"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#8b5cf6', width: 32, height: 32 }}
        onClick={() => {
          setPasswordResetTarget(rowData);
          setIsPasswordModalOpen(true);
        }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: 32, height: 32 }}
        onClick={() => handleDeleteStudent(rowData.user_id)}
      />
    </div>
  );

  const emptyState = getEmptyStateContent();

  // Current-page students, mapped into the flat shape ExportStudentsZip
  // expects (nested profile_picture already flattened by mapStudentRow).
  const exportStudents = useMemo(() => students.map(toExportRow), [students]);

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          @keyframes slideIn   { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          @keyframes bulkBarIn { from { opacity:0; transform:translateY(-6px)  } to { opacity:1; transform:translateY(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-datatable .p-datatable-tbody > tr.row-selected { background:rgba(59,130,246,.1)!important; box-shadow:inset 4px 0 0 0 #3b82f6!important; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
          .p-datatable .p-datatable-tbody > tr > td { padding:16px 12px!important; border-bottom:1px solid #f3f4f6!important; }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .search-input-wrapper { display:flex; align-items:center; gap:10px; background:white; border-radius:16px; padding:12px 18px; margin-bottom:20px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); transition:border-color .2s,box-shadow .2s; }
          .search-input-wrapper:focus-within { border-color:rgba(59,130,246,.4); box-shadow:0 2px 12px rgba(30,64,175,.14); }
          .search-input-wrapper input[type="text"] { flex:1; border:none; outline:none; font-size:13px; font-weight:500; color:#1f2937; background:transparent; }
          .search-input-wrapper input[type="text"]::placeholder { color:#9ca3af; font-weight:400; }
          .search-clear-btn { background:none; border:none; cursor:pointer; color:#9ca3af; padding:2px 4px; border-radius:4px; display:flex; align-items:center; transition:color .2s; }
          .search-clear-btn:hover { color:#6b7280; }
          .bulk-action-bar { animation:bulkBarIn .22s ease; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; background:linear-gradient(135deg,#1e40af,#3b82f6); border-radius:14px; padding:12px 18px; margin-bottom:16px; box-shadow:0 4px 16px rgba(59,130,246,.3); }
          .bulk-count { font-size:13px; font-weight:700; color:white; }
          .bulk-count span { opacity:.75; font-weight:500; }
          .bulk-action-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all .15s; }
          .bulk-action-btn.primary { background:white; color:#1e40af; }
          .bulk-action-btn.primary:hover { background:#eff6ff; }
          .bulk-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
          .bulk-action-btn.ghost:hover { background:rgba(255,255,255,.25); }
          .bulk-action-btn.mark { background:linear-gradient(135deg,#059669,#10b981); color:white; box-shadow:0 2px 8px rgba(16,185,129,.35); }
          .bulk-action-btn.mark:hover { background:linear-gradient(135deg,#10b981,#34d399); }
          .bulk-action-btn.swap { background:linear-gradient(135deg,#f59e0b,#d97706); color:white; box-shadow:0 2px 8px rgba(245,158,11,.35); }
          .bulk-action-btn.swap:hover { background:linear-gradient(135deg,#fbbf24,#f59e0b); }
          .bulk-action-btn.swap:disabled { opacity:.45; cursor:not-allowed; }
          .select-mode-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; transition:all .2s; border:1.5px solid; }
          .select-mode-btn.off { background:white; color:#4169E1; border-color:#c7d2fe; }
          .select-mode-btn.off:hover { background:#eff6ff; }
          .select-mode-btn.on { background:#4169E1; color:white; border-color:#4169E1; }
        `}</style>

        <GlobalHeaderTitle
          title="Students Directory"
          subtitle={`${paginationData.total_items} total students`}
          icon="pi pi-users"
          buttons={[
            ...(showDevTools
              ? [
                  {
                    label: 'Bulk Register',
                    icon: 'pi pi-upload',
                    onClick: () => bulkRegisterRef.current?.open(),
                    outlined: true,
                    disabled: !currentSessionId,
                  },
                ]
              : []),
            {
              label: 'Reorder Roll No',
              icon: 'pi pi-sort-alpha-down',
              onClick: handleReorderRollNumbers,
              outlined: true,
              disabled: !selectedClass || loading,
              style: { borderWidth: '2px' },
            },
            {
              label: 'Add Student',
              icon: 'pi pi-plus',
              onClick: () => {
                setEditingStudent(null);
                setIsRegisterModalOpen(true);
              },
            },
          ]}
        />

        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onClassesLoaded={handleClassesLoaded}
          allowAllSections
          showingAllSections={showAllSections}
          onShowAllSections={handleShowAllSections}
        />

        <div className="search-input-wrapper">
          <i className="pi pi-search" style={{ fontSize: 15, color: '#3b82f6', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search by name, admission no, father name, or roll no..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear-btn" onClick={() => handleSearchChange('')}>
              <i className="pi pi-times" style={{ fontSize: 12 }} />
            </button>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="bulk-action-bar">
            <div className="bulk-count">
              {selectedIds.size} student{selectedIds.size !== 1 ? 's' : ''} selected{' '}
              <span>of {students.length} on this page</span>
              {selectedIds.size === 2 && (
                <span
                  style={{
                    marginLeft: 10,
                    padding: '2px 8px',
                    borderRadius: 12,
                    background: 'rgba(245,158,11,.25)',
                    color: '#fef3c7',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  💡 Swap photos available
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="bulk-action-btn primary" onClick={() => setIsPreModalOpen(true)}>
                <i className="pi pi-pencil" style={{ fontSize: 12 }} /> Bulk Edit
              </button>

              {/* Marks the current selection for export — persists even after you
                  switch class/section, so you can build up a cross-class export set. */}
              <button className="bulk-action-btn mark" onClick={handleAddSelectedToCart}>
                <i className="pi pi-bookmark" style={{ fontSize: 12 }} /> Add to Export Cart
              </button>

              {selectedIds.size === 2 && (
                <button
                  className="bulk-action-btn swap"
                  onClick={() => setIsSwapModalOpen(true)}
                  title={`Swap profile pictures between ${swapStudentA?.name} and ${swapStudentB?.name}`}
                >
                  <i className="pi pi-sync" style={{ fontSize: 12 }} /> Swap Photos
                </button>
              )}

              <button className="bulk-action-btn ghost" onClick={clearSelection}>
                <i className="pi pi-times" style={{ fontSize: 12 }} /> Clear Selection
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {students.length > 0 && (
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: '20px 20px 0 0',
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                    Showing {((studentsPage - 1) * pageSize) + 1}–
                    {Math.min(studentsPage * pageSize, paginationData.total_items)} of{' '}
                    {paginationData.total_items} students
                    {debouncedSearch.trim() && <span style={{ color: '#3b82f6' }}> for "{debouncedSearch}"</span>}
                    {showAllSections && !debouncedSearch.trim() && (
                      <span style={{ color: '#10b981' }}> (all sections)</span>
                    )}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button className={`select-mode-btn ${selectionMode ? 'on' : 'off'}`} onClick={toggleSelectionMode}>
                    <i className="pi pi-check-square" style={{ fontSize: 12 }} />
                    {selectionMode ? 'Exit Select' : 'Select'}
                  </button>

                  <button
                    className="select-mode-btn off"
                    onClick={() => setIsCartPanelOpen(true)}
                    disabled={cartCount === 0}
                    style={{ opacity: cartCount === 0 ? 0.5 : 1, cursor: cartCount === 0 ? 'not-allowed' : 'pointer' }}
                  >
                    <i className="pi pi-bookmark" style={{ fontSize: 12 }} />
                    View Cart{cartCount > 0 ? ` (${cartCount})` : ''}
                  </button>

                  <ExportStudentsZip
                    ref={exportZipRef}
                    students={exportStudents}
                    totalItems={paginationData.total_items}
                    fetchAllStudents={fetchAllStudentsForExport}
                    label={cartCount > 0 ? `Export ZIP (${cartCount} marked)` : 'Export ZIP'}
                    disabled={students.length === 0}
                  />

                  <div
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                      border: '1px solid rgba(65,105,225,.15)',
                    }}
                  >
                    <span style={{ fontSize: 13, color: '#4169E1', fontWeight: 600 }}>
                      Page {paginationData.current_page} of {paginationData.total_pages}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <DataTable
              key={tableKey}
              value={displayRows}
              dataKey="id"
              loading={false}
              rowClassName={(rowData: StudentData & { __selected?: boolean }) =>
                rowData.__selected ? 'row-selected' : ''
              }
              emptyMessage={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: 400,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                      boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                    }}
                  >
                    <i className={emptyState.icon} style={{ fontSize: 25, color: 'white' }} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      color: '#1e40af',
                      fontSize: 20,
                      fontWeight: 700,
                      marginBottom: 8,
                      textAlign: 'center',
                    }}
                  >
                    {emptyState.title}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center',
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {emptyState.message}
                  </p>
                </div>
              }
            >
              {selectionMode && (
                <Column header={checkboxHeader} body={checkboxTemplate} style={{ width: 52, textAlign: 'center' }} />
              )}
              <Column header="#" body={serialNumberTemplate} style={{ width: 52 }} />
              <Column header="Student Name" body={studentNameTemplate} />
              <Column header="Gender" body={genderTemplate} style={{ width: 90 }} />
              <Column header="Father Name" body={fatherNameTemplate} />
              <Column header="Contact" body={contactTemplate} />
              <Column header="Roll No" body={rollNoTemplate} style={{ width: 80 }} />
              <Column header="Admission No" body={admissionNoTemplate} />
              <Column header="Admission Type" body={admissionTypeTemplate} />
              <Column header="Class" body={classTemplate} style={{ width: 80 }} />
              <Column header="Section" body={sectionTemplate} style={{ width: 90 }} />
              <Column header="DOB" body={dobTemplate} />
              <Column header="Address" body={addressTemplate} style={{ width: 200 }} />
              <Column header="Actions" body={actionTemplate} style={{ width: 140 }} />
            </DataTable>

            {paginationData.total_pages > 1 && (
              <CustomPaginator
                page={paginationData.current_page}
                totalPages={paginationData.total_pages}
                totalRecords={paginationData.total_items}
                pageSize={pageSize}
                onPageChange={(newPage) => setStudentsPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setStudentsPage(1);
                }}
                showInfoBar={false}
              />
            )}
          </div>
        )}
      </div>

      <ViewStudentModal open={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} studentId={selectedStudentId} />

      <RegisterStudentModal
        open={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSuccess={(shouldResetPage = false) => {
          setIsRegisterModalOpen(false);
          setEditingStudent(null);
          if (shouldResetPage) setStudentsPage(1);
          fetchStudents();
        }}
        studentData={editingStudent}
      />

      <BulkEditPreModal
        open={isPreModalOpen}
        onClose={() => setIsPreModalOpen(false)}
        studentCount={selectedIds.size}
        onConfirm={handlePreModalConfirm}
      />

      <BulkEditStudentsModal
        open={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        onSuccess={handleBulkEditSuccess}
        students={selectedStudents}
        visibleFields={bulkVisibleFields}
      />

      <PasswordResetModal
        visible={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setPasswordResetTarget(null);
        }}
        onSubmit={handlePasswordReset}
        studentName={passwordResetTarget?.name ?? ''}
        userId={passwordResetTarget?.user_id ?? ''}
      />

      {showDevTools && (
        <BulkRegisterStudentsCSV
          ref={bulkRegisterRef}
          apiClient={apiClient}
          sessionId={currentSessionId}
          session={currentSessionLabel}
          hideTrigger
          onSuccess={() => fetchStudents()}
        />
      )}

      <SwapProfilePictureModal
        open={isSwapModalOpen}
        onClose={() => setIsSwapModalOpen(false)}
        onSuccess={handleSwapSuccess}
        studentA={swapStudentA ?? null}
        studentB={swapStudentB ?? null}
      />

      <ExportCartPanel
        open={isCartPanelOpen}
        onClose={() => setIsCartPanelOpen(false)}
        onExportNow={() => {
          setIsCartPanelOpen(false);
          exportZipRef.current?.open('cart');
        }}
      />
    </>
  );
};

// Flattens a StudentData row into the shape ExportStudentsZip / the export
// cart both expect. Pure function — kept outside the component so it's
// referentially stable across renders.
function toExportRow(s: StudentData): StudentExportData {
  return {
    id: s.id,
    sp_student_id: s.sp_student_id,
    user_id: s.user_id,
    name: s.name,
    father_name: s.father_name,
    mother_name: s.mother_name,
    address: s.address,
    father_no: s.father_no,
    mother_no: s.mother_no,
    gender: s.gender,
    profile_picture_url: s.profile_picture_url,
    admission_no: s.admission_no,
    class_id: s.class_id,
    class_name: s.class_name,
    section: s.section,
    roll_no: s.roll_no,
    email_id: s.email_id,
    dob: s.dob,
    blood_group: s.blood_group,
    admission_type: s.admission_type,
  };
}

export default StudentsListDashboard;